import {
    CreateExtArgs,
    Extension,
    ExtensionAPI,
    IndexKey,
    pointerUtils,
    DmApis,
    DAL,
    DalValue
} from '@wix/document-manager-core'
import type {
    CompRef,
    CompVariantPointer,
    DeserializationMappers,
    NamespaceOverrideFilter,
    Pointer,
    RemoveOverridesOptions,
    SlotsDataItem
} from '@wix/document-services-types'
import _ from 'lodash'
import {
    COMP_DATA_QUERY_KEYS,
    MASTER_PAGE_ID,
    VARIANTABLE_DATA_TYPES,
    DATA_TYPES,
    VIEW_MODES,
    REF_ARRAY_DATA_TYPE,
    RELATION_DATA_TYPES,
    MULTILINGUAL_TYPES
} from '../constants/constants'
import type {ComponentAfterAddDataEvent} from '../hooks'
import {isRepeatedComponent, getRepeaterItems, getUniqueDisplayedId} from '../utils/repeaterUtils'
import {updateVariantRelations} from '../utils/variantsUtils'
import {
    getPointerWithoutFallbacksFromPointer,
    RefDelimiter,
    extractBaseComponentByNamespace,
    extractBaseComponentId
} from '../utils/refStructureUtils'
import {EVENTS as COMPONENT_EVENTS} from './components/components'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {createItemGetter, type DataExtensionAPI} from './data'
import {namespaceMapping} from '@wix/document-services-json-schemas'
import {MOBILE_SPLITTABLE_TYPE, createRefCompQuery, createReferredId, getItemQueryId} from '../utils/inflationUtils'
import {ReportableError} from '@wix/document-manager-utils'
import type {VariantsExtensionAPI} from './variants/variants'
import type {NicknamesExtensionAPI} from './nicknames'
import type {ComponentCodeExtensionAPI} from './componentCode'
import {getTranslationInfoFromKey} from '../utils/translationUtils'
import type {SlotsExtensionAPI} from './slots/slots'

const {NAMESPACES_WITH_OVERRIDES} = namespaceMapping
const {getRepeatedItemPointerIfNeeded} = pointerUtils

const OVERRIDE_HOST_INDEX = 'OVERRIDE_HOST_INDEX'
const TRANSLATIONS_OVERRIDE_HOST_INDEX = 'TRANSLATIONS_OVERRIDE_HOST_INDEX'
const OVERRIDE_TARGET_INDEX = 'OVERRIDE_TARGET_INDEX'

const NO_MATCH: string[] = []
export interface RefOverridesAPI {
    removeOverrides(compPointer: CompRef, options?: RemoveOverridesOptions): void
    componentRemovalOverrides: {
        save(compPointer: CompRef, overrides: Pointer[]): void
        get(compPointer: Pointer): Pointer[]
        clear(compPointer: Pointer): void
    }
    getOverrideId(refCompId: string, compId: string): string
    getOverride(refCompId: string, innerComponentIds: string[], namespace: string, options?: GetSetOverrideOptions): any
    setOverride(
        refCompId: string,
        innerComponentIds: string[],
        namespace: string,
        item: any,
        options?: GetSetOverrideOptions
    ): void
    removeOverride(
        refCompId: string,
        innerComponentIds: string[],
        namespace: string,
        options?: GetSetOverrideOptions
    ): void
    createOverrideDataItem(
        refCompId: Pointer,
        innerComponentId: string,
        itemType: string,
        pageId: string,
        item: Record<string, any>,
        isMobile?: boolean,
        originalNicknameContext?: CompRef | undefined,
        mappers?: DeserializationMappers
    ): void
    nickname: {
        setNicknameContextGenerator(provider: NicknameContextGeneratorProvider): void
        getNicknameContextGenerator(): NicknameContextGeneratorProvider
    }
}

export type RefOverridesExtensionAPI = ExtensionAPI & {
    refOverrides: RefOverridesAPI
}
export type NicknameContextGeneratorProvider = (data: ComponentAfterAddDataEvent) => void

export interface GetSetOverrideOptions {
    mobileSplitItem?: boolean
}

const createExtension = (): Extension => {
    const getOverridesByType = (dal: DAL, dataType: string, queryFilter: IndexKey) => {
        const overrides = dal.query(dataType, queryFilter)
        return _(overrides)
            .toPairs()
            .map(([key, {metaData}]) => createItemGetter(dataType)(key, _.get(metaData, ['pageId'], MASTER_PAGE_ID)))
            .value()
    }
    const getOverridesFilter = (dal: DAL, compPtr: Pointer) =>
        dal.queryFilterGetters[OVERRIDE_HOST_INDEX](getRepeatedItemPointerIfNeeded(compPtr).id)
    const getTranslationOverridesFilter = (dal: DAL, compPtr: Pointer) =>
        dal.queryFilterGetters[TRANSLATIONS_OVERRIDE_HOST_INDEX](getRepeatedItemPointerIfNeeded(compPtr).id)
    const getAllOverrides = (dal: DAL, queryFilter: IndexKey) => {
        return _.reduce(
            NAMESPACES_WITH_OVERRIDES,
            (result, dataType) => _.concat(result, getOverridesByType(dal, dataType, queryFilter)),
            [] as Pointer[]
        )
    }

    const getOverridesTargetingComp = (dal: DAL, compPtr: Pointer, namespace: string) => {
        const queryFilter = dal.queryFilterGetters[OVERRIDE_TARGET_INDEX](compPtr.id)
        return dal.getIndexPointers(queryFilter, namespace)
    }

    const getAllOverrideHosts = (dal: DAL) => {
        return dal.getIndexKeys(OVERRIDE_HOST_INDEX)
    }

    const createExtensionAPI = ({
        pointers,
        eventEmitter,
        extensionAPI,
        dal
    }: CreateExtArgs): RefOverridesExtensionAPI => {
        let nicknameContextProvider: NicknameContextGeneratorProvider
        const componentRemovalOverridesToRemove: Record<string, Pointer[]> = {}

        const {variants} = extensionAPI as VariantsExtensionAPI
        const {data: dataApi} = extensionAPI as DataExtensionAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI

        const shouldExcludeOverrideByNamespace = (filter: NamespaceOverrideFilter | undefined, itemType: string) =>
            filter === '*' || !!filter?.has(itemType)

        const shouldExcludeOverride = (
            overridePointer: Pointer,
            isMobile: boolean,
            options: RemoveOverridesOptions
        ): boolean => {
            const {exclusions, inclusions, removeMobilePropsOnly} = options
            const namespaceFilter = inclusions || exclusions
            const {type} = overridePointer

            if (removeMobilePropsOnly && isMobile) {
                return type !== DATA_TYPES.prop
            }
            const itemType = dal.getWithPath(overridePointer, 'type')
            const shouldExclude = shouldExcludeOverrideByNamespace(namespaceFilter?.[type], itemType)

            return exclusions ? shouldExclude : !shouldExclude
        }

        const getOverrides = (compPointer: CompRef, options: RemoveOverridesOptions = {}): Pointer[] | undefined => {
            const {exclusions, inclusions, removeMobilePropsOnly} = options
            if (exclusions && inclusions) {
                throw new ReportableError({
                    errorType: 'BothExclusionsAndInclusionsProvidedToRemoveOverrides',
                    message: 'Cannot provide both exclusions and inclusions to removeOverrides options.'
                })
            }

            const overrides = getAllOverrides(dal, getOverridesFilter(dal, compPointer))
            const isMobile = pointers.structure.isMobile(compPointer)
            if (exclusions || inclusions || (removeMobilePropsOnly && isMobile)) {
                return overrides.filter(override => !shouldExcludeOverride(override, isMobile, options))
            }

            return overrides
        }

        const removeOverrides = (compPointer: CompRef | CompVariantPointer, options?: RemoveOverridesOptions) => {
            const {slots} = extensionAPI as SlotsExtensionAPI
            const overrides = getOverrides(compPointer, options)

            if (!overrides) {
                return
            }

            for (const override of overrides) {
                if (override.type === DATA_TYPES.slots) {
                    const slotCompPointer = pointerUtils.getCompPointer(
                        extractBaseComponentId(override),
                        compPointer.type,
                        compPointer
                    )
                    Object.keys(dal.get(override).slots).forEach(slotName => {
                        slots.remove(slotCompPointer, slotName)
                    })
                }
                if (override.type in VARIANTABLE_DATA_TYPES && dataApi.refArray.isRefArray(dal.get(override))) {
                    const remoteCompPointerWithLocalOverrides = pointerUtils.getPointerFromPointer(
                        extractBaseComponentId(override),
                        compPointer
                    )
                    variants.removeRefArrayDataConsideringVariants(
                        remoteCompPointerWithLocalOverrides,
                        override.type,
                        override
                    )
                } else {
                    dataModel.removeItemRecursively(override)
                }
            }
        }

        const getOverrideId = (refCompId: string, namespace: string): string =>
            `${refCompId}-${COMP_DATA_QUERY_KEYS[namespace]}`

        const cleanAndAddOverriddenConnections = (
            pageId: string,
            dataItem: Record<string, any>,
            newDataItemId: string,
            compToAddPointer: CompRef,
            originalNicknameContext?: CompRef | undefined
        ) => {
            const pagePointer = pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP)
            const {componentCode} = extensionAPI as ComponentCodeExtensionAPI
            const {nicknames} = extensionAPI as NicknamesExtensionAPI

            if (originalNicknameContext) {
                componentCode.updateConnectionItemsNickname(
                    dataItem.items,
                    compToAddPointer,
                    pagePointer as CompRef,
                    originalNicknameContext
                )
            }
            dataItem.items = _.reject(
                dataItem.items,
                ({type, role}) =>
                    type === 'WixCodeConnectionItem' && nicknames.hasComponentWithThatNickname(pagePointer, role)
            )

            if (!_.isEmpty(dataItem.items)) {
                dataModel.addItem(dataItem, DATA_TYPES.connections, pageId, newDataItemId)
            }
        }

        const validateGetSetOverrideCall = (namespace: string, isMobileSpecific: boolean) => {
            if (!MOBILE_SPLITTABLE_TYPE[namespace] && isMobileSpecific) {
                throw new ReportableError({
                    errorType: 'AttemptedToGetOrSetMobileSpecificOverrideOnNonMobileSplittableNamespace',
                    message: `Cannot get or set mobile specific override on non mobile splittable namespace: ${namespace}`
                })
            }
        }

        const getOverridePointer = (
            refCompId: string,
            innerComponentIds: string[],
            namespace: string,
            options: GetSetOverrideOptions = {}
        ): Pointer => {
            validateGetSetOverrideCall(namespace, options.mobileSplitItem ?? false)

            const referredId = createReferredId(refCompId, innerComponentIds)
            const referredPointer = pointerUtils.getPointer(
                referredId,
                options.mobileSplitItem ? VIEW_MODES.MOBILE : VIEW_MODES.DESKTOP
            )

            const overrideId = getItemQueryId(referredPointer, namespace)

            return pointerUtils.getPointer(overrideId, namespace)
        }

        const getOverride = (
            refCompId: string,
            innerComponentIds: string[],
            namespace: string,
            options: GetSetOverrideOptions = {}
        ) => {
            const refCompPointer = pointerUtils.getPointer(refCompId, VIEW_MODES.DESKTOP)
            const overridePointer = getOverridePointer(refCompId, innerComponentIds, namespace, options)
            return dataModel.getItem(
                overridePointer.id,
                namespace,
                pointers.structure.getPageOfComponent(refCompPointer).id
            )
        }

        const setOverride = (
            refCompId: string,
            innerComponentIds: string[],
            namespace: string,
            item: any,
            options: GetSetOverrideOptions = {}
        ) => {
            const refCompPointer = pointerUtils.getPointer(refCompId, VIEW_MODES.DESKTOP)

            const overridePointer = getOverridePointer(refCompId, innerComponentIds, namespace, options)
            dataModel.addItem(
                item,
                namespace,
                pointers.structure.getPageOfComponent(refCompPointer).id,
                overridePointer.id
            )
        }

        const setOverrideById = (item: any, namespace: string, pageId: string, overrideId: string) => {
            dataModel.addItem(item, namespace, pageId, overrideId)
        }

        const removeOverride = (
            refCompId: string,
            innerComponentIds: string[],
            namespace: string,
            options: GetSetOverrideOptions = {}
        ) => {
            const overridePointer = getOverridePointer(refCompId, innerComponentIds, namespace, options)
            dataModel.removeItemRecursively(overridePointer)
        }
        const NAMESPACES_TO_COPY = [
            DATA_TYPES.data,
            DATA_TYPES.prop,
            DATA_TYPES.design,
            DATA_TYPES.behaviors,
            DATA_TYPES.variables,
            DATA_TYPES.presets,
            DATA_TYPES.connections,
            DATA_TYPES.mobileHints,
            DATA_TYPES.theme,
            DATA_TYPES.slots
        ]

        const addRepeatedItemsDataOverridesIfNeeded = (
            compPointer: Pointer,
            newDataItemId: string,
            dataItem: Record<string, any>,
            itemType: string,
            pageId: string
        ) => {
            const isRepeatedItem = isRepeatedComponent(compPointer.id)
            const repeaterItems =
                isRepeatedItem && getRepeaterItems({dal, pointers, extensionAPI} as CreateExtArgs, compPointer)
            const dataIdsToHandle = isRepeatedItem
                ? _.map(repeaterItems, itemId => getUniqueDisplayedId(newDataItemId, itemId))
                : []
            _.forEach([newDataItemId, ...dataIdsToHandle], dataId => {
                setOverrideById(dataItem, itemType, pageId, dataId)
            })
        }

        const setSlotsOverride = (
            newDataItemId: string,
            pageId: string,
            dataItem: SlotsDataItem,
            mappers: DeserializationMappers
        ) => {
            dataItem.slots = _.mapValues(
                dataItem.slots,
                (slotTargetId: string) => mappers.oldToNewIdMap[slotTargetId] ?? slotTargetId
            )

            setOverrideById(dataItem, DATA_TYPES.slots, pageId, newDataItemId)
        }

        const createOverrideDataItem = (
            compToAddPointer: Pointer,
            compId: string,
            itemType: string,
            pageId: string,
            dataItem: any,
            isMobile = false,
            originalNicknameContext?: CompRef | undefined,
            mappers: DeserializationMappers = {oldToNewIdMap: {}, variantMapper: []}
        ) => {
            if (dataItem?.type === REF_ARRAY_DATA_TYPE) {
                updateVariantRelations(extensionAPI, compToAddPointer, dataItem, mappers)
                if (dataItem.values.length === 0) {
                    return
                }
            } else if (dataItem?.type === RELATION_DATA_TYPES.VARIANTS) {
                return
            }

            if (_.includes(NAMESPACES_TO_COPY, itemType)) {
                const repeatedTemplatePointer = pointerUtils.getRepeatedItemPointerIfNeeded(compToAddPointer)
                let newDataItemId = createRefCompQuery(repeatedTemplatePointer.id, compId, itemType, isMobile)
                if (itemType === DATA_TYPES.data || itemType === DATA_TYPES.design) {
                    addRepeatedItemsDataOverridesIfNeeded(compToAddPointer, newDataItemId, dataItem, itemType, pageId)
                } else if (itemType === DATA_TYPES.variables) {
                    newDataItemId = createRefCompQuery(repeatedTemplatePointer.id, compId)
                    setOverrideById(dataItem, itemType, pageId, newDataItemId)
                } else if (itemType === DATA_TYPES.connections) {
                    cleanAndAddOverriddenConnections(
                        pageId,
                        dataItem,
                        newDataItemId,
                        repeatedTemplatePointer,
                        originalNicknameContext
                    )
                } else if (itemType === DATA_TYPES.slots) {
                    setSlotsOverride(newDataItemId, pageId, dataItem, mappers)
                } else {
                    setOverrideById(dataItem, itemType, pageId, newDataItemId)
                }
            }
        }

        const getNicknameContextGenerator = () => nicknameContextProvider

        const setNicknameContextGenerator = (provider: NicknameContextGeneratorProvider) => {
            nicknameContextProvider = provider
        }

        const saveComponentRemovalOverrides = (compPointer: CompRef, overrides: Pointer[]) => {
            componentRemovalOverridesToRemove[compPointer.id] = overrides
        }

        const getComponentRemovalOverrides = (compPointer: Pointer): Pointer[] =>
            componentRemovalOverridesToRemove[compPointer.id] || []

        const clearComponentRemovalOverrides = (compPointer: Pointer) => {
            delete componentRemovalOverridesToRemove[compPointer.id]
        }

        eventEmitter.addListener(COMPONENT_EVENTS.COMPONENTS.BEFORE_REMOVE, (compRef: CompRef) =>
            removeOverrides(compRef)
        )
        return {
            refOverrides: {
                removeOverrides,
                componentRemovalOverrides: {
                    save: saveComponentRemovalOverrides,
                    get: getComponentRemovalOverrides,
                    clear: clearComponentRemovalOverrides
                },
                getOverrideId,
                getOverride,
                setOverride,
                removeOverride,
                createOverrideDataItem,
                nickname: {
                    getNicknameContextGenerator,
                    setNicknameContextGenerator
                }
            }
        }
    }
    const getReferredStructurePointers = (dal: DAL): any => ({
        getPointerWithoutFallbacks: getPointerWithoutFallbacksFromPointer,
        getAllOverrides: (compPtr: Pointer) => getAllOverrides(dal, getOverridesFilter(dal, compPtr)),
        getAllTranslationOverrides: (compPtr: Pointer) =>
            getOverridesByType(
                dal,
                MULTILINGUAL_TYPES.multilingualTranslations,
                getTranslationOverridesFilter(dal, compPtr)
            ),
        getOverridesByType: (compPtr: Pointer, dataType: string) =>
            getOverridesByType(dal, dataType, getOverridesFilter(dal, compPtr)),
        getConnectionOverrides: (compPtr: Pointer) =>
            getOverridesByType(dal, NAMESPACES_WITH_OVERRIDES.connections, getOverridesFilter(dal, compPtr)),
        getOverridesTargetingComp: (compPtr: Pointer, namespace: string) =>
            getOverridesTargetingComp(dal, compPtr, namespace),
        getAllOverrideHosts: () => getAllOverrideHosts(dal)
    })
    const createPointersMethods = ({dal}: DmApis) => ({
        referredStructure: getReferredStructurePointers(dal),
        full: {
            referredStructure: getReferredStructurePointers(dal)
        }
    })

    const createFilters = () => {
        //e.g. 'comp1_r_comp2_r_prop' -> ['comp1', 'comp1_r_comp2']
        const getAllCompIdsFromOverrideId = (overrideId: string) => {
            const refDelimCompIds = overrideId.split(RefDelimiter).slice(0, -1)

            return refDelimCompIds.reduce((result: string[], current: string) => {
                const previousId = result[result.length - 1]
                const compId = previousId ? `${previousId}${RefDelimiter}${current}` : current
                result.push(compId)

                return result
            }, [])
        }

        return {
            [OVERRIDE_HOST_INDEX]: (namespace: string, value: DalValue, id: string): string[] => {
                if (value && NAMESPACES_WITH_OVERRIDES[namespace] && id.includes(RefDelimiter)) {
                    return getAllCompIdsFromOverrideId(id)
                }
                return NO_MATCH
            },
            [TRANSLATIONS_OVERRIDE_HOST_INDEX]: (namespace: string, value: DalValue, id: string): string[] => {
                if (value && namespace === MULTILINGUAL_TYPES.multilingualTranslations && id.includes(RefDelimiter)) {
                    return getAllCompIdsFromOverrideId(getTranslationInfoFromKey(id)[1])
                }
                return NO_MATCH
            },
            [OVERRIDE_TARGET_INDEX]: (namespace: string, value: DalValue, id: string): string[] => {
                if (value && NAMESPACES_WITH_OVERRIDES[namespace] && id.includes(RefDelimiter)) {
                    return extractBaseComponentByNamespace(id, namespace).split(RefDelimiter).slice(1)
                }
                return NO_MATCH
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {refOverrides} = extensionAPI as RefOverridesExtensionAPI
        return {
            refOverrides: {
                getOverride: refOverrides.getOverride,
                setOverride: refOverrides.setOverride,
                removeOverride: refOverrides.removeOverride
            }
        }
    }

    return {
        name: 'refOverrides',
        dependencies: new Set(['structure', 'dataModel', 'data', 'variants']),
        createExtensionAPI,
        createPointersMethods,
        createFilters,
        createPublicAPI
    }
}

export {createExtension}
